import clsx from "clsx";
import { FC, ReactNode } from "react";

import type { ContentData } from "@tsmono/inspect-common/types";
import { isRecord } from "@tsmono/util";

import { RecordTree } from "../../content/RecordTree";

import { CompactionData, kCompactionMetadata } from "./CompactionData";
import styles from "./ContentDataView.module.css";
import { FallbackData, kFallbackMetadata } from "./FallbackData";
import { WebSearch } from "./WebSearch";
import { isWebSearchContentData, WebSearchResults } from "./WebSearchResults";

export interface ContentDataProps {
  id: string;
  contentData: ContentData;
}

interface RenderableData {
  type?: string;
  name?: string;
  [key: string]: unknown;
}

export const ContentDataView: FC<ContentDataProps> = ({ id, contentData }) => {
  const renderableData = contentData.data as RenderableData;

  const renderer = contentDataRenderers.find((r) =>
    r.canRender(renderableData)
  );

  if (!renderer) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { encrypted_content, ...record } = renderableData;
    return (
      <div className={clsx(styles.contentData)}>
        <RecordTree id={`${id}-tree`} record={record} defaultExpandLevel={0} />
      </div>
    );
  }

  return (
    <div className={clsx(styles.contentData)}>
      {renderer.render(id, renderableData)}
    </div>
  );
};

interface ContentDataRenderer {
  name: string;
  canRender: (data: RenderableData) => boolean;
  render: (id: string, data: RenderableData) => ReactNode;
}

const compactionDataRenderer: ContentDataRenderer = {
  name: "Compaction",
  canRender: (data: RenderableData) => {
    return Object.hasOwn(data, kCompactionMetadata);
  },
  render: (id: string, data: RenderableData): ReactNode => {
    return <CompactionData id={id} data={data} />;
  },
};

const fallbackDataRenderer: ContentDataRenderer = {
  name: "Fallback",
  canRender: (data: RenderableData) => {
    return Object.hasOwn(data, kFallbackMetadata);
  },
  render: (_id: string, data: RenderableData): ReactNode => {
    return <FallbackData data={data} />;
  },
};

const webSearchServerToolRenderer: ContentDataRenderer = {
  name: "WebSearch",
  canRender: (data: RenderableData) => {
    return data.type === "server_tool_use" && data.name === "web_search";
  },
  render: (_id: string, data: RenderableData): ReactNode => {
    const input = data.input;
    const query = isRecord(input) ? input["query"] : undefined;
    return <WebSearch query={typeof query === "string" ? query : ""} />;
  },
};

const webSearchResultsServerToolRenderer: ContentDataRenderer = {
  name: "WebSearchResults",
  canRender: (data: RenderableData) => {
    return (
      data.type === "web_search_tool_result" && Array.isArray(data.content)
    );
  },
  render: (_id: string, data: RenderableData): ReactNode => {
    const results = Array.isArray(data.content)
      ? data.content.filter(isWebSearchContentData)
      : [];
    return <WebSearchResults results={results} />;
  },
};

const serverToolRenderer: ContentDataRenderer = {
  name: "ServerTool",
  canRender: (data: RenderableData) => data.type === "server_tool_use",
  render: (id: string, data: RenderableData): ReactNode => {
    return (
      <>
        <div
          className={clsx(
            "text-style-label",
            "text-style-secondary",
            "text-size-smaller"
          )}
        >
          Server Tool
        </div>
        <RecordTree id={`${id}-server-tool`} record={data} />
      </>
    );
  },
};

export const contentDataRenderers: ContentDataRenderer[] = [
  compactionDataRenderer,
  fallbackDataRenderer,
  webSearchServerToolRenderer,
  webSearchResultsServerToolRenderer,
  serverToolRenderer,
];
